import { faBan, faPen, faSave } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createUseStyles } from 'react-jss'
import { Colors } from '../constants/colors'

interface Props {
  value?: string
  placeholder?: string
  validationRegex?: RegExp
  onEdited: (value: string) => void
  rows?: number
}

const EditableTextArea = ({ value, placeholder, validationRegex, onEdited, rows = 4 }: Props) => {
  const styles = useStyles()
  const { t } = useTranslation('common')
  const [editMode, setEditMode] = useState(false)
  const [editFieldValue, setEditFieldValue] = useState<string | undefined>(value)
  const [valid, setValid] = useState(true)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const autoResize = () => {
    if (!textareaRef.current) return
    const el = textareaRef.current
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }

  useEffect(() => {
    setEditFieldValue(value)
    setValid(true)
  }, [value])

  useEffect(() => {
    if (editMode) {
      textareaRef?.current?.focus()
      autoResize()
    }
  }, [editMode])

  useEffect(() => {
    if (editMode) {
      autoResize()
    }
  }, [editFieldValue, editMode])

  const onCancel = () => {
    setEditMode(false)
    setEditFieldValue(value)
    setValid(true)
  }

  const validate = () => {
    if (validationRegex) {
      if (editFieldValue?.match(validationRegex)) {
        setValid(true)
        return true
      }
      setValid(false)
      return false
    }
    return true
  }

  const onSave = () => {
    if (!validate()) return

    setEditMode(false)
    onEdited(editFieldValue ?? '')
    setValid(true)
  }

  const onEdit = () => {
    setEditMode(true)
  }

  const renderReadonlyText = () => {
    const text = (value == null || value.length < 1) ? (placeholder ?? '') : value
    return <span>{text}</span>
  }

  return (
    <div className={styles.container}>
      {!editMode && (
        <div className={styles.readonlyContainer}>
          <div className={styles.readonlyText}>
            {renderReadonlyText()}
          </div>
          <div className={styles.iconContainer}>
            <FontAwesomeIcon
              className={styles.button}
              icon={faPen}
              color={Colors.gray1}
              onClick={onEdit}
              title={t('edit')}
            />
          </div>
        </div>
      )}
      {editMode && (
        <div className={styles.editContainer}>
          <textarea
            ref={textareaRef}
            className={`${styles.textarea} ${!valid ? styles.invalid : ''}`}
            rows={rows}
            value={editFieldValue ?? ''}
            onChange={e => setEditFieldValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') {
                e.preventDefault()
                onCancel()
              }
            }}
          />
          <div className={styles.iconContainer}>
            <FontAwesomeIcon
              className={styles.button}
              icon={faBan}
              color={Colors.gray1}
              onClick={onCancel}
              title={t('cancel')}
            />
            <FontAwesomeIcon
              className={styles.button}
              icon={faSave}
              color={Colors.gray1}
              onClick={onSave}
              title={t('save')}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default EditableTextArea

const useStyles = createUseStyles({
  container: {
    width: '100%',
    position: 'relative',
  },
  readonlyContainer: {
    position: 'relative',
    width: '100%',
  },
  readonlyText: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    paddingRight: 40,
    minHeight: 60,
  },
  editContainer: {
    position: 'relative',
    width: '100%',
  },
  textarea: {
    width: '100%',
    overflow: 'hidden',
    resize: 'none',
    padding: '8px 40px 8px 8px',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    borderRadius: 4,
    border: `1px solid ${Colors.gray2}`,
    boxSizing: 'border-box',
    '&:focus': {
      outline: 'none',
      borderColor: Colors.brandGreen,
      boxShadow: `0 0 0 2px rgba(0, 123, 255, 0.25)`,
    },
  },
  invalid: {
    borderColor: Colors.rubel_400,
  },
  iconContainer: {
    position: 'absolute',
    top: 4,
    right: 4,
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  button: {
    cursor: 'pointer',
  },
})
